import React, { useRef } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { personalInfo } from '../data/portfolio';

// Animated statement word — reveals on scroll
const AnimatedWord = ({ word, delay, isVisible, accent = false }) => (
  <div
    className="font-bold leading-none tracking-tight"
    style={{
      fontSize: 'clamp(2.5rem, 6vw, 5.5rem)',
      color: accent ? 'var(--color-accent)' : 'var(--color-text)',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      transition: `opacity 0.6s ease ${delay}ms, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
    }}
  >
    {word}
  </div>
);

const About = () => {
  const { ref: headRef, isVisible: headVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: wordsRef, isVisible: wordsVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: bioRef, isVisible: bioVisible } = useScrollReveal({ threshold: 0.15 });
  const { ref: certRef, isVisible: certVisible } = useScrollReveal({ threshold: 0.1 });

  const methodWords = [
    { word: 'THINK.', delay: 0 },
    { word: 'BUILD.', delay: 120, accent: true },
    { word: 'SHIP.', delay: 240 },
    { word: 'ITERATE.', delay: 360 },
  ];

  return (
    <section
      id="about"
      className="py-24 md:py-36"
      style={{ backgroundColor: 'var(--color-bg2)', borderTop: '1px solid var(--color-border)' }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Section label */}
        <div ref={headRef}>
          <div
            className="section-label mb-8"
            style={{
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.5s ease, transform 0.5s ease',
            }}
          >
            01 / ABOUT
          </div>

          <h2
            className="font-bold leading-none tracking-tight mb-4"
            style={{
              fontSize: 'clamp(2.2rem, 6vw, 5rem)',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
            }}
          >
            I DON'T JUST
          </h2>
          <h2
            className="font-bold leading-none tracking-tight"
            style={{
              fontSize: 'clamp(2.2rem, 6vw, 5rem)',
              color: 'var(--color-accent)',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
            }}
          >
            WRITE CODE.
          </h2>
        </div>

        {/* Method words */}
        <div
          ref={wordsRef}
          className="flex flex-wrap gap-6 md:gap-10 mt-16 mb-16"
        >
          {methodWords.map(({ word, delay, accent }) => (
            <AnimatedWord
              key={word}
              word={word}
              delay={delay}
              isVisible={wordsVisible}
              accent={accent}
            />
          ))}
        </div>

        {/* Two-column: bio + certifications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Bio */}
          <div ref={bioRef}>
            <p
              className="text-base md:text-lg leading-relaxed mb-8"
              style={{
                color: 'var(--color-text2)',
                opacity: bioVisible ? 1 : 0,
                transform: bioVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              {personalInfo.bio}
            </p>

            {/* Stats row */}
            <div
              className="grid grid-cols-2 gap-6"
              style={{
                opacity: bioVisible ? 1 : 0,
                transform: bioVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.6s ease 0.15s, transform 0.6s ease 0.15s',
              }}
            >
              {personalInfo.stats.map((stat, i) => (
                <div
                  key={i}
                  className="py-4"
                  style={{ borderTop: '1px solid var(--color-border)' }}
                >
                  <div
                    className="text-2xl font-bold mb-1"
                    style={{ color: 'var(--color-text)' }}
                  >
                    {stat.value}
                  </div>
                  <div className="section-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div ref={certRef}>
            <div
              className="section-label mb-5"
              style={{
                opacity: certVisible ? 1 : 0,
                transition: 'opacity 0.5s ease',
              }}
            >
              CERTIFICATIONS
            </div>
            <div className="space-y-3">
              {[
                { name: 'AWS Certified Solution Architect — Associate', issuer: 'Amazon Web Services', year: '2023' },
                { name: 'Explore Generative AI with Vertex AI Gemini API Skill Badge', issuer: 'Google Cloud', year: '2024' },
                { name: 'Build Real World AI Applications with Gemini & Imagen Skill Badge', issuer: 'Google Cloud', year: '2024' },
                { name: 'Prompt Design in Vertex AI Skill Badge', issuer: 'Google Cloud', year: '2024' },
                { name: 'Inspect Rich Documents with Gemini Multimodality & Multimodal RAG Skill Badge', issuer: 'Google Cloud', year: '2024' },
                { name: 'Develop GenAI Apps with Gemini and Streamlit Skill Badge', issuer: 'Google Cloud', year: '2024' },
                { name: 'Cloud Computing', issuer: 'NPTEL', year: '2023' },
              ].map((cert, i) => (
                <div
                  key={i}
                  className="flex items-start justify-between py-3"
                  style={{
                    borderBottom: '1px solid var(--color-border)',
                    opacity: certVisible ? 1 : 0,
                    transform: certVisible ? 'translateX(0)' : 'translateX(20px)',
                    transition: `opacity 0.5s ease ${i * 80}ms, transform 0.5s ease ${i * 80}ms`,
                  }}
                >
                  <div>
                    <div
                      className="text-sm font-medium mb-1"
                      style={{ color: 'var(--color-text)' }}
                    >
                      {cert.name}
                    </div>
                    <div className="section-label">{cert.issuer}</div>
                  </div>
                  <div
                    className="font-mono text-xs ml-4 flex-shrink-0"
                    style={{ color: 'var(--color-accent)' }}
                  >
                    {cert.year}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
